/**
 * Строка вопроса клиента — общая для «Вопросов клиентов» списком и внутри группы (`KnowledgeQuestionGroups`):
 * число упоминаний, текст-ссылка на страницу вопроса, категория, вид, «без полного ответа», карточка, каналы,
 * мой статус, микрофон. `words` — слова поиска для подсветки, `actions` — кнопки редактора под строкой.
 * `compact` — формулировка внутри группы (третий уровень): мельче, без категории (она у группы), малый микрофон.
 * `onCards` — «карточка есть» открывает карточки в панели справа (`CardPanel`), а не страницу.
 */
import { Mic } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { channelName, type Question } from "@/lib/knowledge";
import { MINE, mineTone } from "@/pages/KnowledgeQuestionPage";

/** Слова поиска как на сервере (`questions.слова_поиска`): нижний регистр, ё→е, окончания длинных срезаны. */
export function searchWords(q: string): string[] {
  return q
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter((w) => w.length >= 2)
    .map((w) => (w.length >= 7 ? w.slice(0, -2) : w.length >= 5 ? w.slice(0, -1) : w));
}

/** Подсветка слов поиска в тексте (е в слове совпадает и с ё). */
export function Highlight({ text, words }: { text: string; words: string[] }) {
  if (!words.length) return <>{text}</>;
  const re = new RegExp(`(${words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/е/g, "[её]")).join("|")})`, "giu");
  return (
    <>
      {text.split(re).map((part, i) =>
        i % 2 ? (
          <mark key={i} className="rounded-sm bg-accent/25 text-fg">
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </>
  );
}

export function QuestionRow({
  x,
  href,
  category,
  kindLabel,
  canEdit,
  words,
  onRemember,
  onVoice,
  actions,
  compact = false,
  onCards,
}: {
  x: Question;
  href: string;
  category: string;
  kindLabel: string | undefined;
  canEdit: boolean;
  words: string[];
  onRemember: () => void;
  onVoice: () => void;
  actions?: ReactNode;
  compact?: boolean;
  onCards?: (cards: { id: number; title: string }[]) => void;
}) {
  return (
    <li
      id={`kbq-${x.id}`}
      className={`group flex items-start hover:bg-bg-subtle focus-within:bg-bg-subtle ${compact ? "gap-2.5 py-1.5 pl-2 pr-3" : "gap-3 px-3 py-2"}`}
    >
      <span
        className={`shrink-0 pt-0.5 text-right tabular-nums ${compact ? "w-7 text-[12.5px] text-fg-muted" : "w-9 text-[15px] text-fg"}`}
        title="Диалогов с этим вопросом за период"
      >
        {x.hits}
      </span>
      <div className="min-w-0 flex-1">
        <Link to={href} onClick={onRemember} className={`text-fg hover:underline ${compact ? "text-[12.5px]" : "text-[13px] font-medium"}`}>
          <Highlight text={x.text} words={words} />
        </Link>
        <div className={`flex flex-wrap gap-x-3 text-fg-muted ${compact ? "text-[11px]" : "mt-0.5 text-[11.5px]"}`}>
          {!compact && <span>{category}</span>}
          {x.kind && x.kind !== "knowledge" && (
            <span className="rounded border border-warn bg-warn/10 px-1 text-fg" title="Отвечать не карточкой, а данными или действием">
              {kindLabel ?? x.kind}
            </span>
          )}
          {x.unanswered > 0 && <span className="text-warn">без полного ответа {x.unanswered}</span>}
          {x.cards.length > 0 ? (
            onCards ? (
              <button type="button" className="text-ok underline-offset-2 hover:underline" title={x.cards.map((k) => k.title).join(" · ")} onClick={() => onCards(x.cards)}>
                {x.cards.length > 1 ? `карточек ${x.cards.length}` : "карточка есть"}
              </button>
            ) : (
              <span className="text-ok" title={x.cards.map((k) => k.title).join(" · ")}>
                карточка есть
              </span>
            )
          ) : (
            <span className="text-fg-faint">карточки нет</span>
          )}
          {canEdit && x.answers > 0 && <span>ответов сотрудников: {x.answers}</span>}
          <span className="text-fg-faint">{x.channels.map(channelName).join(", ")}</span>
        </div>
        {actions}
      </div>
      <span className={`hidden shrink-0 pt-0.5 text-right sm:block ${compact ? "w-28 text-[11.5px]" : "w-36 text-[12px]"} ${mineTone(x.mine_status)}`}>
        {x.mine_status === "done" || x.mine_status === "used" ? "✓ " : ""}
        {MINE[x.mine_status]}
      </span>
      <button
        type="button"
        className={`grid shrink-0 place-items-center rounded-[var(--radius-ui)] border border-border text-fg-muted group-hover:border-accent group-hover:text-accent group-focus-within:border-accent group-focus-within:text-accent ${compact ? "size-7" : "size-9"}`}
        onClick={onVoice}
        aria-label={`Ответить голосом: ${x.text}`}
        title="Ответить голосом — запись начнётся сразу"
      >
        <Mic size={compact ? 13 : 15} />
      </button>
    </li>
  );
}
